import React, { useState, useMemo } from 'react';
import { DialogHandlerProps } from 'hooks/useDialogHandler';
import { useConversationList } from 'apis/CompanyAPI/conversations/useConversationList';
import { useConversationRequestList } from 'apis/CompanyAPI/conversations/useConversationRequestList';
import Resources from 'util/resource/Resources';
import { Conversation, UserConversation } from 'apis/CompanyAPI/conversations/conversationTypes';
import NewConversationButton from '../NewConversationButton';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import Tab from 'ui/modules/tabs/Tab';
import Tabs from 'ui/modules/tabs/Tabs';
import MessageList from './MessageList';
import { useUserProfiles } from 'apis/CompanyAPI/users/useUserProfiles';
import { useCompanyProfiles } from 'apis/CompanyAPI/companies/useCompanyProfiles';
import { getOrUndefined } from 'util/resource';
import flatten from 'ramda/src/flatten';
import uniq from 'ramda/src/uniq';
import { notEmpty } from 'util/arrayUtils';
import { ICompany } from 'types/company';

interface Props {
  setActiveChat: (conversation: Conversation) => void;
  dialogHandler: DialogHandlerProps;
  onCompanyProfilesLoaded?: (companyProfiles: ICompany[]) => void;
}

// Helper function to extract participant user IDs from conversations
function getConversationParticipants(conversations: Conversation[]) {
  const userConversationsWith = flatten(
    conversations
      .map(v =>
        v.type === 'user-user'
          ? v.userConversation.activeParticipants.concat(v.userConversation.inactiveParticipants)
          : undefined,
      )
      .filter(notEmpty),
  );
  const latestMessagesUsers = conversations.map(v => v.latestReply.creatorCwUserId);
  return uniq(userConversationsWith.concat(latestMessagesUsers));
}

export default function UserInboxContent(props: Props) {
  const { setActiveChat, onCompanyProfilesLoaded } = props;
  const { resource: userProfileResource } = useSelfUserProfile();
  const [activeTab, setActiveTab] = useState<'messages' | 'requests'>('messages');

  // Use SWR hooks for conversation data
  const { resource: latestConversations } = useConversationList({ limit: 30 });
  const { resource: latestConversationRequests } = useConversationRequestList({ limit: 30 });

  // Extract user IDs for profile fetching
  const conversationUserIds = useMemo(() => {
    const conversations = getOrUndefined(latestConversations)?.values ?? [];
    const requests = getOrUndefined(latestConversationRequests)?.values ?? [];
    const allConversations = [...conversations, ...requests];
    return getConversationParticipants(allConversations);
  }, [latestConversations, latestConversationRequests]);

  const userProfiles = useUserProfiles(conversationUserIds);

  const companyIds = useMemo(() => {
    const conversations = getOrUndefined(latestConversations)?.values ?? [];
    const requests = getOrUndefined(latestConversationRequests)?.values ?? [];
    const allConversations = [...conversations, ...requests];

    return Array.from(
      new Set(allConversations.filter(c => c.type === 'user-company').map(c => c.companyConversation.companyId)),
    );
  }, [latestConversations, latestConversationRequests]);

  const { resource: companyProfilesResource } = useCompanyProfiles(companyIds);
  const companyProfiles = getOrUndefined(companyProfilesResource)?.values ?? [];

  // Notify parent component when company profiles are loaded
  React.useEffect(() => {
    if (onCompanyProfilesLoaded && companyProfiles.length > 0) {
      onCompanyProfilesLoaded(companyProfiles);
    }
  }, [companyProfiles, onCompanyProfilesLoaded]);

  return (
    <div className="u-content-padding">
      <div className="u-flex-align-center u-flex-space-between">
        <h3 className="text-h3">Inbox</h3>
        <NewConversationButton />
      </div>
      <Resources resources={[latestConversations, latestConversationRequests, userProfileResource]}>
        {([conversations, requests, userProfile]) => (
          <>
            <div>
              <Tabs className="u-content-spacing-y" activeTab={activeTab}>
                <Tab
                  onClick={() => setActiveTab('messages')}
                  value="messages"
                  label="Messages"
                  hasActivityIndicator={conversations.values.some((c: Conversation) => c.hasUnreadMessages)}
                />
                <Tab
                  onClick={() => setActiveTab('requests')}
                  value="requests"
                  label="Requests"
                  hasActivityIndicator={requests.values.some((r: UserConversation) => r.hasUnreadMessages)}
                />
              </Tabs>
            </div>
            {activeTab === 'messages' && (
              <MessageList
                companyProfiles={companyProfiles}
                userProfiles={userProfiles}
                conversations={conversations}
                fetchConversations={() => {}}
                cwUserId={userProfile.cwUserId}
                setActiveChat={setActiveChat}
                type="messages"
              />
            )}
            {activeTab === 'requests' && (
              <MessageList
                companyProfiles={companyProfiles}
                userProfiles={userProfiles}
                conversations={requests}
                fetchConversations={() => {}}
                cwUserId={userProfile.cwUserId}
                setActiveChat={setActiveChat}
                type="requests"
              />
            )}
          </>
        )}
      </Resources>
    </div>
  );
}
